/**
 * Design Tokens / Color Constants
 * 
 * This file centralizes all color definitions used throughout the application.
 * These values should match the CSS variables in global.css and theme.ts
 * 
 * Use these constants in TypeScript/JavaScript code (especially Three.js)
 * where CSS variables cannot be directly accessed.
 */

// ========================
// Background colors
// ========================
export const BG_COLORS = {
    primary: '#1e1e1e',
    secondary: '#252526',
    tertiary: '#2d2d30',
    viewport: '#2a2a2a',
    overlay: 'rgba(0, 0, 0, 0.7)',
    overlayLight: 'rgba(0, 0, 0, 0.5)',
} as const;

// ========================
// Text colors
// ========================
export const TEXT_COLORS = {
    primary: '#e0e0e0',
    secondary: '#a0a0a0',
    muted: '#707070',
} as const;

// ========================
// Status colors
// ========================
export const STATUS_COLORS = {
    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#00aaff',
} as const;

// ========================
// Selection colors
// ========================
export const SELECTION_COLORS = {
    window: '#0066ff',
    windowFill: 'rgba(0, 102, 255, 0.1)',
    crossing: '#00cc00',
    crossingFill: 'rgba(0, 204, 0, 0.1)',
    highlight: '#00aaff',
    hover: '#ffaa00',
} as const;

// ========================
// Snap colors
// ========================
export const SNAP_COLORS = {
    endpoint: '#00ff00',
    midpoint: '#00ffff',
    center: '#ff00ff',
    intersection: '#ffff00',
    perpendicular: '#00aaff',
    tangent: '#ff8800',
    quadrant: '#ff0088',
    nearest: '#88ff00',
    node: '#ffffff',
    grid: '#888888',
} as const;

// ========================
// Grid and Axis Colors
// ========================
// GRID_COLORS holds the dark-mode reference values (they mirror the
// --color-grid-* tokens in global.css). Live viewports read the tokens
// through useViewportThemeColors() instead, so the drawn grid follows the
// color mode; these constants remain as the hook's fallbacks.
export const GRID_COLORS = {
    major: '#4a4a4a',
    minor: '#3a3a3a',
    section: '#6a6a6a',
} as const;

/**
 * Sketch workbench canvas references (they mirror the --color-*-sketch
 * tokens in global.css). The sketch canvas is deliberately mode-independent:
 * its edge inks (SKETCH_STYLE_COLORS) are always dark, so the light
 * SketchUp-style ground stays light in dark mode too.
 */
export const SKETCH_VIEWPORT_COLORS = {
    background: '#bfbfc6',
    gridMajor: '#a9a9b1',
    gridMinor: '#d2d2d9',
    gridSection: '#6a6a6a',
} as const;

/**
 * Viewport ink: the default line color for model/detail/annotation geometry
 * (dark-mode reference value mirroring --color-ink-viewport in global.css).
 * White lines on the dark viewport, black on the light one — live viewports
 * read the token through useAnnotationInk(); this constant stays as the
 * hook's fallback.
 */
export const INK_COLORS = {
    viewport: '#ffffff',
} as const;

export const AXIS_COLORS = {
    x: '#ff4444',
    y: '#44ff44',
    z: '#4444ff',
} as const;

// ========================
// Sketch workbench (freeform modeling)
// ========================
export const SKETCH_COLORS = {
    primary: '#e04938',
    secondary: '#b03628',
    accent: '#ff7a63',
    // Inference feedback (Sketch language: green endpoint, cyan midpoint,
    // magenta on-axis, teal on-face).
    inferenceEndpoint: '#00ff44',
    inferenceMidpoint: '#00ffff',
    inferenceOnAxis: '#ff00ff',
    inferenceOnFace: '#22cccc',
    inferenceCenter: '#ff00ff',
    faceHover: '#ffd700',
    faceSelected: '#ff9500',
    guide: '#22aaff',
    sectionPlane: '#8844ff',
} as const;

/** Display-style face/edge colors (hiddenLine / monochrome / wireframe / xray). */
export const SKETCH_STYLE_COLORS = {
    hiddenFace: '#ffffff',
    hiddenEdge: '#1a1a1a',
    monoFace: '#b9b9b4',
    monoEdge: '#3a3a3a',
    wireEdge: '#2a2a2a',
    shadedEdge: '#333330',
    /** Sketch-style backface tint: dark steel-blue tone for reversed/interior
     *  faces (the SketchUp backface language — shading stays visible on it). */
    backface: '#7b93a6',
} as const;

/**
 * Paint Bucket palette: named model materials (content data, not UI chrome).
 * The first entry is the default material for new solids.
 */
export const SKETCH_MATERIALS: Array<{ id: string; name: string; color: string }> = [
    { id: 'concrete', name: 'Concrete', color: '#b8b8b0' },
    { id: 'brick', name: 'Brick', color: '#a8503c' },
    { id: 'wood', name: 'Wood', color: '#8a6642' },
    { id: 'steel', name: 'Steel', color: '#6a737d' },
    { id: 'glass', name: 'Glass', color: '#9fd4dd' },
    { id: 'roof', name: 'Roof Tile', color: '#7a3b2e' },
    { id: 'grass', name: 'Grass', color: '#6a9955' },
    { id: 'asphalt', name: 'Asphalt', color: '#4a4a4a' },
    { id: 'white', name: 'White', color: '#e8e8e4' },
    { id: 'black', name: 'Black', color: '#2f2f2f' },
    { id: 'red', name: 'Red', color: '#c0392b' },
    { id: 'blue', name: 'Blue', color: '#2e6da4' },
] as const;

export const sketchMaterialColor = (materialId: string): string =>
    SKETCH_MATERIALS.find((m) => m.id === materialId)?.color ?? SKETCH_MATERIALS[0].color;

// ========================
// Element preview colors
// ========================
export const PREVIEW_COLORS = {
    // Viewport background is dark (#2a2a2a) in dark mode and light gray
    // (#d0d0d0) in light mode — use the shared accent blue, which reads on
    // both, instead of white (invisible on the light viewport).
    wall: '#00aaff',
    door: '#8b4513',
    window: '#87ceeb',
    column: '#0078d4',
    stair: '#0000ff',
    ramp: '#00ff00',
    railing: '#ff0000',
    ceiling: '#ffa500',
    floor: '#808080',
    roof: '#8b4513',
    curtainWall: '#00ffff',
    beam: '#800080',
    mep: '#ffff00',
    measure: '#00aaff',
    move: '#00aaff',
    line: '#00aaff',
    modelText: '#00aaff',
    foundation: '#a0a0a0',
    level: '#2f7bd6',
    grid: '#c9520f',
} as const;

// ========================
// Border colors
// ========================
export const BORDER_COLORS = {
    base: '#3e3e42',
    light: 'rgba(255, 255, 255, 0.2)',
    focus: '#0078d4',
} as const;

// Type exports for type-safety
export type SnapColorType = keyof typeof SNAP_COLORS;
export type SelectionColorType = keyof typeof SELECTION_COLORS;
export type PreviewColorType = keyof typeof PREVIEW_COLORS;
